import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  ApiResponse,
  PageableDto,
  PageResponse,
} from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';
import {
  mapMutationMessage,
  mapPageableResponse,
} from '../../../utils/map-pageable';
import {
  buildCountryTitleListParams,
  normalizeCountryTitleStatus,
  toCountryTitleApiPayload,
} from '../../../utils/country-title-catalog.utils';

export interface BankEntity {
  id: string;
  name: string;
  code?: string;
  status?: string;
}

/** @deprecated Usar `PageResponse<BankEntity>`. */
export type BankEntityPageResponse = PageResponse<BankEntity>;

/**
 * Entidades bancarias por país.
 * Endpoint: `/settings/countries/{countryId}/bank-titles`
 */
@Injectable({ providedIn: 'root' })
export class BankEntityService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private resource(countryId: string, id?: string | number): string {
    const base = `${this.apiUrl}/${countryId}/bank-titles`;
    return id !== undefined && id !== null ? `${base}/${id}` : base;
  }

  private mapToBankEntity(it: Record<string, unknown>): BankEntity {
    return {
      id: String(it['id'] ?? ''),
      name: (it['title'] as string) || (it['name'] as string) || '',
      code: (it['code'] as string) || '',
      status: normalizeCountryTitleStatus(it['status'] as string | undefined),
    };
  }

  getBankEntities(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<PageResponse<BankEntity>> {
    const params = buildCountryTitleListParams({
      page,
      size,
      sort,
      direction,
      search,
      status,
    });

    return this.httpClient
      .get<ApiResponse<PageableDto>>(this.resource(countryId), { params })
      .pipe(
        map((response) =>
          mapPageableResponse(
            response,
            (item) => this.mapToBankEntity(item as Record<string, unknown>),
            size,
          ),
        ),
      );
  }

  getBankEntityById(countryId: string, id: string | number) {
    return this.httpClient
      .get<ApiResponse<Record<string, unknown>>>(this.resource(countryId, id))
      .pipe(
        map((resp) =>
          this.mapToBankEntity((resp.data ?? {}) as Record<string, unknown>),
        ),
      );
  }

  createBankEntity(countryId: string, payload: Record<string, unknown>) {
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        this.resource(countryId),
        toCountryTitleApiPayload(payload as any),
      )
      .pipe(
        map((res) => mapMutationMessage(res, 'Entidad bancaria creada')),
      );
  }

  updateBankEntity(
    countryId: string,
    id: string | number,
    payload: Record<string, unknown>,
  ) {
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        this.resource(countryId, id),
        toCountryTitleApiPayload(payload as any),
      )
      .pipe(
        map((res) => mapMutationMessage(res, 'Entidad bancaria actualizada')),
      );
  }

  deleteBankEntity(countryId: string, id: string | number) {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(
        this.resource(countryId, id),
      )
      .pipe(
        map((res) => mapMutationMessage(res, 'Entidad bancaria eliminada')),
      );
  }
}
